import { GridLayoutItem } from "@progress/kendo-react-layout";
import uniqueId from "lodash-es/uniqueId";
import { Trash2 } from "lucide-react";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import IconButton from "@/components/Button/IconButton";
import { useConditionBuilderContext } from "@/components/ConditionBuilder/context/ConditionBuilderContext";
import {
  type ConditionEntry,
  type ConditionLeaf,
} from "@/components/ConditionBuilder/types";
import { ActionDropDown } from "@/components/Kendo/Popup";
import Duplicate from "@/icons/duplicate.svg?react";
import NestedCondition from "@/icons/nested-condition.svg?react";

const TrashSVG = (props: React.ComponentProps<typeof Trash2>) => (
  <Trash2 color="var(--tlx-status-error-text)" {...props} />
);

interface ConditionRowActionsProps {
  index: number;
  arrayName: string;
  onRemove: (index: number) => void;
  onInsert: (index: number, value: ConditionLeaf) => void;
  onUpdate: (index: number, value: ConditionEntry) => void;
}

const ConditionRowActions: React.FC<ConditionRowActionsProps> = ({
  index,
  arrayName,
  onRemove,
  onInsert,
  onUpdate,
}) => {
  const { config } = useConditionBuilderContext();
  const methods = useFormContext();

  // Convert via useFieldArray.update (passed from parent ConditionGroup) so
  // useFieldArray's internal `fields` state stays in sync with the new entry
  // type. methods.setValue alone would mutate the array element in place
  // without updating fields, leaving the row stuck as a leaf in the UI.
  const handleConvertToNested = () => {
    const path = `${arrayName}.${index}` as never;
    const currentLeaf = methods.getValues(path) as ConditionLeaf;
    onUpdate(index, {
      id: uniqueId("group_"),
      combinator: "AND",
      conditions: [{ ...currentLeaf }],
    } as ConditionEntry);
  };

  const handleDuplicate = () => {
    const path = `${arrayName}.${index}` as never;
    const currentLeaf = methods.getValues(path) as ConditionLeaf;
    onInsert(index + 1, { ...currentLeaf, id: uniqueId("cond_") });
  };

  return (
    <GridLayoutItem className="condition-builder__row-actions">
      {/* Read-only views show no edit affordances at all — a greyed-out trash
          icon still reads as "this row can be deleted". The cell itself stays
          mounted so the grid columns line up with the editable variant. */}
      {!config.readOnly && (
        <IconButton
          Icon={TrashSVG}
          fillMode="flat"
          type="button"
          title="Remove condition"
          onClick={event => {
            event.preventDefault();
            onRemove(index);
          }}
        />
      )}
      {!config.readOnly && (
        <ActionDropDown
          className="action-button"
          actions={[
            {
              title: "Convert to Nested Condition",
              ICon: NestedCondition,
              hidden: !config.allowNestedConditions,
              onClick: handleConvertToNested,
            },
            {
              title: "Duplicate",
              ICon: Duplicate,
              onClick: handleDuplicate,
            },
          ]}
        />
      )}
    </GridLayoutItem>
  );
};

export default ConditionRowActions;
